import React from "react";

import {
  Card,
  EmptyState,
  InfoDot,
  Segmented,
  Skeleton,
  Table,
} from "../../design";
import { getUseCases } from "../../lib/api";
import type { AuthHeaders } from "../../lib/auth";
import { getHashParam, setHashParam } from "../../lib/hashState";
import type {
  DateRange,
  InsightFilters,
  UseCase,
  UseCaseAxis,
  UseCases,
} from "../../lib/types";
import { humanize, pct, usdShort, useLoad } from "./util";

const OCCUPATION: Record<string, string> = {
  management: "Management",
  business_financial: "Business & finance",
  computer_mathematical: "Software & data",
  architecture_engineering: "Engineering",
  life_physical_social_science: "Science & research",
  community_social_service: "Social services",
  legal: "Legal",
  education_library: "Education",
  arts_design_media: "Design & media",
  healthcare_practitioners: "Healthcare",
  healthcare_support: "Healthcare support",
  sales: "Sales",
  office_admin_support: "Office & support",
  other_occupation: "Other work",
  non_work: "Personal use",
};

const COLLABORATION: Record<string, string> = {
  directive: "Hands off whole tasks",
  feedback_loop: "Relays errors back",
  task_iteration: "Iterates together",
  learning: "Asks to learn",
  validation: "Checks own work",
  none: "No clear pattern",
};

// O*NET work activity names, shortened; the full name stays on hover.
const ACTIVITY: Record<string, string> = {
  "Getting Information": "Getting information",
  "Monitoring Processes, Materials, or Surroundings": "Monitoring processes",
  "Estimating the Quantifiable Characteristics of Products, Events, or Information":
    "Estimating quantities",
  "Identifying Objects, Actions, and Events": "Identifying events",
  "Inspecting Equipment, Structures, or Materials": "Inspecting equipment",
  "Analyzing Data or Information": "Analyzing data",
  "Evaluating Information to Determine Compliance with Standards":
    "Checking compliance",
  "Judging the Qualities of Objects, Services, or People": "Judging quality",
  "Processing Information": "Processing information",
  "Developing Objectives and Strategies": "Setting strategy",
  "Making Decisions and Solving Problems": "Solving problems",
  "Organizing, Planning, and Prioritizing Work": "Planning work",
  "Scheduling Work and Activities": "Scheduling",
  "Thinking Creatively": "Creative thinking",
  "Updating and Using Relevant Knowledge": "Keeping knowledge current",
  "Documenting/Recording Information": "Documenting",
  "Drafting, Laying Out, and Specifying Technical Devices, Parts, and Equipment":
    "Technical drafting",
  "Working with Computers": "Working with computers",
  "Repairing and Maintaining Electronic Equipment": "Repairing electronics",
  "Repairing and Maintaining Mechanical Equipment": "Repairing machinery",
  "Controlling Machines and Processes": "Controlling machines",
  "Handling and Moving Objects": "Handling objects",
  "Operating Vehicles, Mechanized Devices, or Equipment": "Operating vehicles",
  "Performing General Physical Activities": "Physical work",
  "Assisting and Caring for Others": "Caring for others",
  "Communicating with People Outside the Organization":
    "Talking to outside people",
  "Communicating with Supervisors, Peers, or Subordinates":
    "Talking to colleagues",
  "Establishing and Maintaining Interpersonal Relationships":
    "Building relationships",
  "Interpreting the Meaning of Information for Others": "Explaining to others",
  "Performing for or Working Directly with the Public":
    "Working with the public",
  "Resolving Conflicts and Negotiating with Others": "Negotiating",
  "Selling or Influencing Others": "Selling and persuading",
  "Coaching and Developing Others": "Coaching",
  "Coordinating the Work and Activities of Others": "Coordinating others",
  "Developing and Building Teams": "Building teams",
  "Guiding, Directing, and Motivating Subordinates": "Leading people",
  "Providing Consultation and Advice to Others": "Giving advice",
  "Training and Teaching Others": "Teaching",
  "Monitoring and Controlling Resources": "Managing resources",
  "Performing Administrative Activities": "Admin work",
  "Staffing Organizational Units": "Staffing",
};

const AXES: {
  value: UseCaseAxis;
  label: string;
  title: string;
  info: string;
}[] = [
  {
    value: "occupation",
    label: "Field",
    title: "By field of work",
    info: "The occupation the task in each conversation belongs to.",
  },
  {
    value: "activity",
    label: "Activity",
    title: "By activity",
    info: "The kind of work the conversation is doing, from O*NET's work activities. Hover a row for the full name.",
  },
  {
    value: "collaboration",
    label: "Working style",
    title: "By working style",
    info: "How the person works with the AI, judged from their own turns only.",
  },
];

function useCaseLabel(axis: UseCaseAxis, label: string): string {
  if (axis === "occupation") return OCCUPATION[label] ?? label;
  if (axis === "collaboration") return COLLABORATION[label] ?? label;
  return ACTIVITY[label] ?? label;
}

function isAxis(v: string | null | undefined): v is UseCaseAxis {
  return AXES.some((a) => a.value === v);
}

/** Conversations and spend per classifier label, on one taxonomy axis. */
export function UseCasesView({
  authHeaders,
  base,
  range,
  filters = {},
  org,
}: {
  authHeaders: AuthHeaders;
  base: string;
  range: DateRange;
  filters?: InsightFilters;
  org: boolean;
}) {
  const [axis, setAxisState] = React.useState<UseCaseAxis>(() => {
    const h = getHashParam("axis");
    return isAxis(h) ? h : "occupation";
  });
  const setAxis = (a: UseCaseAxis) => {
    setAxisState(a);
    setHashParam("axis", a === "occupation" ? null : a);
  };
  const { data, error } = useLoad(
    () => getUseCases(authHeaders, base, range, axis, filters),
    [base, range.start, range.end, axis, JSON.stringify(filters)],
  );
  const meta = AXES.find((a) => a.value === axis) ?? AXES[0];
  const info = org
    ? `${meta.info} condense labels each conversation from its first messages. Labels are shown only in aggregate, and any label used by fewer than ${data?.min_people ?? 3} people is grouped with the rest.`
    : `${meta.info} condense labels each conversation from its first messages.`;

  let body: React.ReactNode;
  if (error)
    body = (
      <EmptyState compact icon="alert" title="Couldn't load use cases">
        {error}
      </EmptyState>
    );
  else if (!data)
    body = (
      <div className="ds-card-pad">
        <Skeleton lines={5} />
      </div>
    );
  else if (!data.classified)
    body = (
      <EmptyState compact icon="lock" title="Conversations aren't labelled">
        {org ? "This organization has" : "You have"} zero data retention on, or
        no classifier is deployed here, so condense never reads conversations to
        label them.
      </EmptyState>
    );
  else if (data.labelled === 0)
    body = (
      <EmptyState
        compact
        icon="sparkle"
        title={
          data.conversations > 0
            ? "No labelled conversations yet"
            : "No conversations in this range"
        }
      >
        {data.conversations > 0
          ? "Conversations get a label after their first few messages. Check back in a few minutes."
          : "Use cases appear once requests go through condense."}
      </EmptyState>
    );
  else body = <UseCaseTable axis={axis} org={org} data={data} />;

  return (
    <Card
      flush
      title={meta.title}
      info={info}
      actions={
        <Segmented
          size="sm"
          label="Group use cases by"
          value={axis}
          onChange={setAxis}
          options={AXES.map((a) => ({ value: a.value, label: a.label }))}
        />
      }
    >
      {body}
    </Card>
  );
}

function UseCaseTable({
  axis,
  org,
  data,
}: {
  axis: UseCaseAxis;
  org: boolean;
  data: UseCases;
}) {
  const rows = rowsOf(data, axis);
  const counted = rows.reduce((n, r) => n + r.u.conversations, 0);
  const covered = coverage(data);
  return (
    <>
      <Table label={`Use cases by ${axis}`}>
        <thead>
          <tr>
            <th className="ana-namecol">Use case</th>
            <th className="num">Conversations</th>
            <th className="num hide-sm">Share</th>
            {org && (
              <th className="num hide-md">
                <span className="ana-th">
                  People
                  <InfoDot text="People who had these conversations themselves. Traffic from org API keys is your product's, so it isn't counted." />
                </span>
              </th>
            )}
            <th className="num">Spend</th>
            <th className="num hide-sm">
              <span className="ana-th">
                Saved
                <InfoDot text="What condense took off the bill for these conversations." />
              </span>
            </th>
          </tr>
        </thead>
        <tbody>
          {rows.map(({ key, name, full, u, muted }) => {
            const share = counted ? u.conversations / counted : 0;
            return (
              <tr key={key}>
                <td
                  className={`ana-namecol${muted ? " muted" : " strong"}`}
                  title={full}
                >
                  {name}
                </td>
                <td className="num">{humanize(u.conversations)}</td>
                <td className="num hide-sm">
                  <span className="ana-share">
                    <span className="ana-share-track" aria-hidden>
                      <span
                        style={{
                          width: `${Math.min(100, share * 100)}%`,
                          background: muted
                            ? "var(--ds-other)"
                            : "var(--ds-s1)",
                        }}
                      />
                    </span>
                    <span>{pct(share)}</span>
                  </span>
                </td>
                {org && (
                  <td className="num hide-md">
                    {!u.people ? (
                      <span className="muted">—</span>
                    ) : (
                      humanize(u.people)
                    )}
                  </td>
                )}
                <td className={`num${muted ? "" : " strong"}`}>
                  {usdShort(u.cost_usd)}
                </td>
                <td className="num hide-sm">
                  {key === UNLABELLED ? (
                    <span className="muted">—</span>
                  ) : (
                    usdShort(u.saved_usd)
                  )}
                </td>
              </tr>
            );
          })}
        </tbody>
      </Table>
      <div className="ana-pager">
        <span className="ds-meta">
          {humanize(data.labelled)} of {humanize(data.conversations)}{" "}
          {data.conversations === 1 ? "conversation" : "conversations"} labelled
          in this range
          {covered &&
            `. Labels cover ${usdShort(covered.labelled)} of the ${usdShort(covered.total)} spent in conversations (${pct(covered.share)}).`}
        </span>
      </div>
    </>
  );
}

const UNLABELLED = "__unlabelled__";

/** How much of the conversation spend in range the labels cover; null
 * while the API doesn't say. */
export function coverage(
  data: UseCases,
): { labelled: number; total: number; share: number } | null {
  if (data.cost_usd == null || data.unlabelled_cost_usd == null) return null;
  const total = Number(data.cost_usd);
  if (!(total > 0)) return null;
  const labelled = Math.max(0, total - Number(data.unlabelled_cost_usd));
  return { labelled, total, share: labelled / total };
}

export function rowsOf(
  data: UseCases,
  axis: UseCaseAxis,
): { key: string; name: string; full: string; u: UseCase; muted: boolean }[] {
  const out = data.groups.map((u) => ({
    key: u.label,
    name: useCaseLabel(axis, u.label),
    full: axis === "activity" ? u.label : useCaseLabel(axis, u.label),
    u,
    muted: false,
  }));
  if (data.folded)
    out.push({
      key: "__folded__",
      name: `Other, under ${data.min_people} people each`,
      full: `Uses that fewer than ${data.min_people} people had, grouped so no one stands out`,
      u: data.folded,
      muted: true,
    });
  if (data.unlabelled_conversations)
    out.push({
      key: UNLABELLED,
      name: "Not labelled yet",
      full: "Conversations the classifier hasn't labelled: too new, too short, or sent before labelling was on",
      u: {
        label: "",
        conversations: data.unlabelled_conversations,
        people: null,
        requests: data.unlabelled_requests ?? 0,
        cost_usd: data.unlabelled_cost_usd ?? "0",
        saved_usd: "0",
      },
      muted: true,
    });
  return out;
}
